import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import * as Store from '@apply/store';
import { Header } from '../common_v2/components/Header';
import { alertModal, isMobileScreen } from '@apply/ui';
import { applicationInsights } from '../ApplicationInsights';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { FeatureType } from '@apply/constants';
import { Button, ButtonTheme, Panel, PanelBorderRadius, PanelTheme, TextBox, EmailBox, PasswordBox } from '@apply/components';
import { Footer } from '../common_v2/components/Footer';
import { useDispatch } from 'react-redux';
import { LoginApi } from '@apply/api/src/api/loginApi';
import { Authentication } from '@apply/store/src/user/types';
import { authenticatedFetch } from '@apply/store/src/cross';

const reactPlugin = new ReactPlugin();

export const appInsightsApply = import.meta.env.VITE_APPINSIGHTS_CLIENT_APPLY;
const ai = new ApplicationInsights({
    config: {
        instrumentationKey: appInsightsApply,
        enableAutoRouteTracking: true,
        extensions: [reactPlugin],
    },
});
ai.loadAppInsights();

export type AuthenticationHandler = (
    email: string,
    password: string,
    ipaddress: string
) => Promise<Store.UserStore.Types.Authentication | undefined>;

export type UseAuthenticationResult = {
    login: (username: string, password: string, ipaddress: string, handler: AuthenticationHandler) => Promise<boolean>;
    logout: () => void;
    isAuthenticated: boolean;
    isRegisteredUser: boolean;
    authentication: Store.UserStore.Types.Authentication | undefined;
    isLocEnabled: boolean;
    requiresESign: boolean;
    isFeatureEnabled: (featureType: FeatureType) => boolean;
};

export const useAuthentication = (): UseAuthenticationResult => {
    const [authentication, setAuthentication] = useState<Store.UserStore.Types.Authentication>();
    const isAuthenticated = useMemo(() => Store.UserStore.Types.isAuthenticated(authentication), [authentication]);
    const isRegisteredUser = useMemo(() => Store.UserStore.Types.isRegisteredUser(authentication), [authentication]);
    const isLocEnabled = useMemo(() => Store.UserStore.Types.isLocEnabled(authentication), [authentication]) ?? false;
    const requiresESign = useMemo(() => Store.UserStore.Types.requiresESign(authentication), [authentication]) ?? false;

    const isFeatureEnabled = (featureType: FeatureType) => {
        const auth = Store.UserStore.Types.retrieve();
        return Store.UserStore.Types.isFeatureEnabled(auth.user, featureType) ?? false;
    };

    useEffect(() => {
        const stored = Store.UserStore.Types.retrieve();
        if (stored) {
            setAuthentication(stored);
        }
    }, []);

    const login = useCallback(
        async (myUsername: string, password: string, ipaddress: string, handler: AuthenticationHandler): Promise<boolean> => {
            if (!myUsername || !password || !handler) {
                return false;
            }

            try {
                const result = await handler(myUsername, password, ipaddress);

                if (!result) {
                    return false;
                }

                Store.UserStore.Types.store(result);
                setAuthentication(result);
                return true;
            } catch {
                return false;
            }
        },
        []
    );

    const logout = useCallback(() => {
        const stackTrace = Error().stack;
        ai.appInsights.trackTrace({ message: 'logout' }, { stackTrace });
        Store.UserStore.Types.clear();
        window.sessionStorage.clear();
        setAuthentication(undefined);
    }, []);

    return {
        login,
        logout,
        isAuthenticated,
        isRegisteredUser,
        authentication,
        isLocEnabled,
        requiresESign,
        isFeatureEnabled,
    };
};

export const LoginPageV2: React.FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const loginApi = new LoginApi(applicationInsights);

    const { from } = location.state || { from: { pathname: '/borrower' } };

    const queryParams = new URLSearchParams(location.search);
    const requestedEmail = queryParams.get('email');

    const [email, setEmail] = useState<string>(requestedEmail ?? '');
    const [isEmailValid, set_isEmailValid] = useState<boolean>(false);

    const [password, setPassword] = useState<string>('');
    const [isPasswordValid, set_isPasswordValid] = useState<boolean>(false);

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [attempts, setAttempts] = useState<number>(0);
    const [submitError, setSubmitError] = useState<string>();
    const [ipAddress, setIpAddress] = useState<string>('');

    const [redirectToReferrer] = useState<boolean>(false);
    const [redirectToHome, setRedirectToHome] = useState<boolean>(false);
    const [redirectToOther, setRedirectToOther] = useState(false);

    const { logout, login } = useAuthentication();

    const onEnterKey = () => {
        if (email && password && !submitting) {
            handleLogin();
        }
    };

    // Pulled from the Cross Store file so that we don't modify the base for backwards compatability
    // This should be refactored into the Login API as ApplyLogin or similar in the future
    const crossStoreLoginV2 = async (email: string, password: string, ipaddress: string): Promise<Authentication | undefined> => {
        const requestOptions: RequestInit = {
            method: 'POST',
            body: JSON.stringify({
                email,
                password,
                ipaddress,
            }),
        };

        const baseUrl = import.meta.env.VITE_API_URL;

        const response = await authenticatedFetch(`${baseUrl}/account/principal/login`, requestOptions);
        return await Store.CrossStore.processLoginResponse(response);
    };

    const handler = async (email: string, password: string, ipaddress: string) => {
        dispatch(Store.BorrowerStore.Actions.reset());
        dispatch(Store.PropertiesStore.Actions.reset());
        dispatch(Store.FormsStore.Actions.reset());
        const response = await crossStoreLoginV2(email, password, ipaddress);
        return response;
    };

    const handleLogin = async () => {
        /* istanbul ignore if */
        if (!isEmailValid || !isPasswordValid) {
            return;
        }
        setAttempts((prev) => prev + 1);

        setSubmitting(true);
        setError(false);
        setErrorMessage('');

        const result = await login(email, password, ipAddress, handler);

        // istanbul ignore else
        setSubmitting(false);
        if (result) {
            console.log('Log successful login to Application Insights.');
            applicationInsights.appInsights.trackEvent({
                name: `${email} has logged in successfully.`,
            });

            const queryParams = new URLSearchParams(location.search);
            const redirectTo = queryParams.get('redirectTo');
            if (redirectTo == null) {
                setRedirectToHome(true);
            } else {
                setRedirectToOther(true);
            }
        } else {
            setError(true);
            setErrorMessage('email and/or password invalid');
        }
    };

    const fetchIpAddress = async () => {
        const response = await loginApi.getIpAddress();

        if (response.statusCode !== 200) return;

        setIpAddress(response.result);
    };

    useEffect(() => {
        fetchIpAddress();
    }, []);

    useEffect(() => {
        logout();
    }, [logout]);

    useEffect(() => {
        if (submitError) {
            alertModal({
                message: 'An error has occurred while communicating with our server.  Please try your operation again.',
            });
            setSubmitError(undefined);
        }
    }, [submitError]);

    useEffect(() => {
        if (redirectToOther) {
            const queryParams = new URLSearchParams(location.search);
            const redirectTo = queryParams.get('redirectTo') || '';
            navigate(redirectTo);
        }

        if (redirectToReferrer) {
            navigate(from.pathname);
        }

        if (redirectToHome) {
            navigate('/home');
        }
    }, [redirectToOther, redirectToReferrer, redirectToHome]);

    const panelChildren = () => {
        return (
            <>
                <main>
                    {' '}
                    <h2>Let's get started</h2>
                    <div className='login subheader'>
                        <div className='sub-title'>New users please register</div>
                    </div>
                    <div className='login_register-fields'>
                        <EmailBox
                            id='emailAddress'
                            data-testid='login-page-email-address'
                            name='emailAddress'
                            value={email}
                            onChange={(email: string) => setEmail(email)}
                            onEnterKey={onEnterKey}
                            onValidationEvent={(isValid) => set_isEmailValid(isValid)}
                            label='Email Address*'
                            required={true}
                        />
                        <PasswordBox
                            id='password'
                            data-testid='login-page-password'
                            name='password'
                            value={password}
                            onChange={(password) => setPassword(password)}
                            onEnterKey={onEnterKey}
                            onValidationEvent={(isValid) => set_isPasswordValid(isValid)}
                            label='Password*'
                            required={true}
                        />
                    </div>
                    {error && (
                        <div>
                            <Typography style={{ color: 'red' }}>{errorMessage}</Typography>
                        </div>
                    )}
                    {attempts > 3 && (
                        <div
                            data-testid='login-page-max-attempts-message'
                            className='login maxAttemptsExceeded'
                        >
                            <span style={{ paddingTop: '16px' }}>Would you like to</span>
                            <Button
                                id='attempt-exceeded-passwordreset-button'
                                key='attempt-exceeded-passwordreset-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={() => navigate('/forgotpassword')}
                                width='165px'
                            >
                                Reset your password?
                            </Button>
                        </div>
                    )}
                    <div className='login primaryActions'>
                        <Button
                            id='primaryButton'
                            theme={ButtonTheme.Primary}
                            width='80%'
                            disabled={!isEmailValid || !isPasswordValid || submitting}
                            handleClick={handleLogin}
                        >
                            Sign In
                        </Button>
                    </div>
                    <div className='login secondaryActions'>
                        <div style={{ width: '100%' }}>
                            <Button
                                id='register-button'
                                key='register-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={() => navigate('/register')}
                                width='100px'
                            >
                                Register
                            </Button>
                        </div>
                        <div style={{ float: 'right', width: 'auto' }}>
                            <Button
                                id='register-button'
                                key='register-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={() => navigate(`/forgotPassword?email=${email}`)}
                                width='100px'
                            >
                                Can't log in?
                            </Button>
                        </div>
                    </div>
                </main>
            </>
        );
    };
    return (
        <>
            <div className='app-container'>
                <Header />
                <div className='app-container authenticationContainer'>
                    {isMobileScreen() ? (
                        <div className='authenticationContainer mobile'>{panelChildren()}</div>
                    ) : (
                        <div className='authenticationContainer desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width={'90%'}
                            >
                                <div className='authenticationContainer desktop desktopWrapper'>{panelChildren()}</div>
                            </Panel>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};
